<template>
  <div>

    <modal
      :title="$t('titles.manage_spot_rates')"
      @submit="submit"
      :submitting="submitting"
      ref="theModal"
      fullscreen
    >

      <v-container fluid class="h-100" style="max-width: 1600px">

        <location-product-picker
          :location-id="location.ID"
          v-model.number="formState.locationProductId"
          :verticals="[...VerticalRegistry.getMany(['spotRates']), ...VerticalRegistry.getMany(['eventTiers'])].map(v => v.name)"
          :vertical-filter="verticalFilter"
          autofocus
          :rules="[rules!.required!()]"
        />

        <v-alert v-if="!formState.locationProductId">
          Please select a product.
        </v-alert>

        <template v-if="formState.locationProductId && locationProduct">

          <manage-spot-rates-form
            v-if="productRateType === 'spotRates'"
            :location="location"
            :location-product="locationProduct"
            :date-range="dateRange"
            ref="spotRatesComponent"
            @submitted="submitted"
          />

          <manage-event-tiers-form
            v-if="productRateType === 'eventTiers'"
            :location="location"
            :location-product="locationProduct"
            :date="dateRange?.start ?? null"
            ref="eventTiersComponent"
            @submitted="submitted"
          />

        </template>

      </v-container>

    </modal>

  </div>
</template>

<script setup lang="ts">

import { computed, onBeforeMount, reactive, useTemplateRef } from 'vue'
import { useRules } from 'vuetify/labs/rules'

// components
import LocationProductPicker from '../../../../LocationProductPicker.vue'
import ManageSpotRatesForm from './ManageSpotRatesForm.vue'
import ManageEventTiersForm from './ManageEventTiersForm.vue'
import type Modal from '../../../../Modal.vue'

// models
import Location from '../../../../../models/Location'
import LocationProduct from '../../../../../models/LocationProduct'

// misc
import VerticalRegistry from '../../../../../verticals/VerticalRegistry'
import type { DateRange } from '../../../../../types/misc'
// @ts-ignore: don't complain about vue3-promise-dialog's missing typings
import { closeDialog } from 'vue3-promise-dialog'

interface Props {
  location: Location,
  locationProductId?: null | number // we assume that all defaultRates and ChannelDefaultRates for this location are already in the store
  verticalFilter?: null | string
  dateRange?: null | DateRange
}

const props = withDefaults(defineProps<Props>(), {
  locationProductId: null,
  verticalFilter: null,
  dateRange: null,
})

const rules = useRules()

/**
 *
 */

interface FormState {
  locationProductId: null | number
}

const formState = reactive<FormState>({
  locationProductId: null,
})

/**
 *
 */

const locationProduct = computed<null | LocationProduct>(() => {
  return (
    formState.locationProductId ?
      LocationProduct.query().whereId(formState.locationProductId).with(['product']).first() :
      null
  )
})

const productRateType = computed<null | 'spotRates' | 'eventTiers'>(() => {
  const verticalName = locationProduct.value?.product?.verticalType
  if (verticalName) {
    const vertical = VerticalRegistry.get(verticalName)
    if (vertical) {
      if (vertical.supports(['spotRates'])) {
        return 'spotRates'
      }
      if (vertical.supports(['eventTiers'])) {
        return 'eventTiers'
      }
    }
  }
  return null
})

/**
 * Initialization
 */

onBeforeMount(() => {

  if (props.locationProductId) {
    formState.locationProductId = props.locationProductId
  }

})

/**
 * Submission
 * (just delegate to the child component)
 */

const theModal = useTemplateRef<typeof Modal>('theModal')
const spotRatesComponent = useTemplateRef<typeof ManageSpotRatesForm>('spotRatesComponent')
const eventTiersComponent = useTemplateRef<typeof ManageEventTiersForm>('eventTiersComponent')

const childComponent = computed<null | typeof ManageSpotRatesForm | typeof ManageEventTiersForm>(() => {
  if (productRateType.value === 'spotRates') {
    return spotRatesComponent.value
  } else if (productRateType.value === 'eventTiers') {
    return eventTiersComponent.value
  }
  return null
})

const submitting = computed<boolean>(() => {
  return childComponent.value ? childComponent.value.submitting : false
})

async function submit() {

  if (!theModal.value) {
    return
  }

  await theModal.value.modalForm.validate()

  if (!theModal.value.modalForm.isValid) {
    return
  }

  if (childComponent.value) {
    childComponent.value.submit()
  }

}

function submitted() {
  closeDialog(true)
}

</script>
